/**
 * Chat Context
 *
 * Manages global state for the support chat feature.
 * Handles message history, API communication, and UI state.
 */

import React, { createContext, useState, useContext, useCallback } from 'react';
import apiService from '../services/apiService';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface ChatContextType {
  isOpen: boolean;
  messages: ChatMessage[];
  loading: boolean;
  error: string | null;
  openChat: () => void;
  closeChat: () => void;
  sendMessage: (message: string) => Promise<void>;
  clearChat: () => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openChat = useCallback(() => {
    setIsOpen(true);

    // Add welcome message if no messages exist
    if (messages.length === 0) {
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: 'Hi! I\'m here to help you with SendUpdates.ai. What can I assist you with today?',
        timestamp: new Date(),
      }]);
    }
  }, [messages.length]);

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  const sendMessage = useCallback(async (message: string) => {
    if (!message.trim()) return;

    // Add user message immediately
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: message,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.post('/api/chat', { message });

      // Add assistant response
      const assistantMessage: ChatMessage = {
        id: response.data.messageId || (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.data.message,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (err: any) {
      console.error('[ChatContext] Error:', err);
      setError(err.response?.data?.error || 'Failed to send message');

      // Add error message to chat
      const errorMessage: ChatMessage = {
        id: 'error-' + Date.now(),
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again or contact hello@sendupdates.ai.',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  }, []);

  const clearChat = useCallback(async () => {
    try {
      await apiService.post('/api/chat/clear');
      setMessages([]);
      closeChat();
    } catch (err) {
      console.error('[ChatContext] Error clearing chat:', err);
    }
  }, [closeChat]);

  return (
    <ChatContext.Provider
      value={{
        isOpen,
        messages,
        loading,
        error,
        openChat,
        closeChat,
        sendMessage,
        clearChat,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = (): ChatContextType => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within ChatProvider');
  }
  return context;
};
